import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import PageHero from "@/components/feature/PageHero";
import CtaBand from "@/components/feature/CtaBand";
import Reveal from "@/components/base/Reveal";
import SectionHeading from "@/components/feature/SectionHeading";
import usePageMeta from "@/hooks/usePageMeta";
import {
  aboutValues,
  brandTagline,
  contactEmail,
  seoKeywords,
  testimonials,
} from "@/mocks/siteData";

const pageTitle = "关于我们 - 匿名爆料投稿平台定位与内容原则 | 潜声投稿";
const pageDesc =
  "了解潜声投稿的品牌定位、发展理念与内容原则。我们是一个以隐私优先为核心的匿名爆料投稿平台，为图文、视频、录音等线索提供安全通道，并坚持以事实核实为准。";

const jsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: pageTitle,
    description: pageDesc,
    inLanguage: "zh-CN",
    url: "https://qiansheng.online/about",
    mainEntity: {
      "@type": "Organization",
      name: "潜声投稿",
      email: contactEmail,
      description:
        "面向公众的匿名爆料投稿平台，提供图文投稿通道、匿名私信投稿、视频素材上传与录音证据提交四类安全入口。",
      knowsAbout: ["匿名爆料投稿", "视频素材上传", "录音证据提交", "图文投稿通道"],
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://qiansheng.online/" },
      { "@type": "ListItem", position: 2, name: "关于我们", item: "https://qiansheng.online/about" },
    ],
  },
];

const positionings = [
  {
    id: "po1",
    icon: "ri-git-branch-line",
    title: "一个接洽入口，不是一个转发号",
    desc: "我们不生产情绪化的二手内容，只承接一手线索并完成核实。任何一条线索进入后台后，都会经过登记、脱敏、核实三个固定环节。",
  },
  {
    id: "po2",
    icon: "ri-user-shared-line",
    title: "面向普通人，而不是专业爆料人",
    desc: "投稿不需要你会写通稿，也不需要你懂法律术语。把看到的说清楚就够了，剩下的整理与求证由编辑来完成。",
  },
  {
    id: "po3",
    icon: "ri-eye-off-line",
    title: "默认匿名，是设计而不是选项",
    desc: "匿名不是需要你额外申请的特殊待遇，而是平台的默认状态。你要做的事只是决定是否留下联系方式。",
  },
];

const principles = [
  "不刊发无法交叉核实的线索，宁可放弃也不冒进。",
  "不因线索来源不同而区别对待，一律登记、一律脱敏。",
  "不使用煽动性标题放大情绪，不以预设立场组织事实。",
  "不接受任何形式的付费投稿，也不向投稿者索取费用。",
  "刊发后接受更正，核实有误时公开说明修改内容。",
];

export default function About() {
  usePageMeta({
    title: pageTitle,
    description: pageDesc,
    keywords: seoKeywords,
    path: "/about",
    jsonLd,
  });

  return (
    <Layout>
      <PageHero
        eyebrow="关于潜声"
        title="为愿意开口的人，留一条安全的路"
        subtitle="潜声投稿是一个以隐私优先为核心的匿名爆料投稿平台。我们不追求消息有多快，只在意线索是否站得住、投稿者是否安全。"
        image="https://readdy.ai/api/search-image?query=Abstract%20silhouette%20of%20many%20thin%20vertical%20lines%20forming%20a%20unified%20wave%2C%20deep%20teal%20and%20charcoal%20tones%20with%20warm%20amber%20highlights%20at%20the%20crest%2C%20minimal%20editorial%20poster%20art%2C%20cinematic%20depth%2C%20negative%20space%2C%20no%20text%2C%20no%20people&width=1600&height=700&seq=qs-about-a1&orientation=landscape"
        imageAlt="无数细线汇成一道波浪的抽象图形，象征潜声投稿汇集零散线索"
        crumbs={[{ label: "首页", to: "/" }, { label: "关于我们" }]}
      />

      <section className="w-full px-4 md:px-8 py-16 md:py-24">
        <div className="max-w-content mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-6">
            <Reveal>
              <div className="w-full aspect-[4/3] rounded-lg overflow-hidden border border-background-200">
                <img
                  src="https://readdy.ai/api/search-image?query=Abstract%20editorial%20still%20life%20of%20a%20dark%20wooden%20desk%20with%20a%20softly%20glowing%20lamp%2C%20blank%20notebook%20and%20a%20closed%20laptop%2C%20deep%20green%20shadows%2C%20warm%20amber%20light%2C%20minimal%20cinematic%20mood%2C%20no%20text%2C%20no%20people&width=1200&height=900&seq=qs-about-art1&orientation=landscape"
                  alt="深色木质书桌上放着笔记本与台灯，象征潜声投稿编辑核实线索的工作场景"
                  title="品牌介绍 - 潜声投稿"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-6">
            <Reveal delay={80}>
              <SectionHeading
                eyebrow="品牌介绍"
                title="潜声，取意于「潜在的声音」"
                desc="很多重要的事实，一开始只存在于少数人的记忆和手机里。它们没有被说出口，往往不是因为不重要，而是因为说出口的代价太高。"
              />
              <p className="mt-5 text-[15px] leading-relaxed text-foreground-600">
                潜声投稿成立于这样一个朴素的判断：如果投稿的过程足够安全、足够简单，愿意开口的人就会多一点点。
                我们不承诺每条线索都能被刊发，但承诺每一条都会被认真看过、按同一套标准处理。
              </p>
              <p className="mt-4 text-[15px] leading-relaxed text-foreground-600">
                平台目前的日常运作由内容编辑与事实核查人员共同维护，核心工作只有两件：把线索核实清楚，把投稿者保护到位。
                这也是「{brandTagline}」这句话的全部含义。
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-16 md:py-20 bg-background-100">
        <div className="max-w-content mx-auto">
          <Reveal>
            <SectionHeading
              eyebrow="网站定位"
              title="我们想成为什么样的平台"
              align="center"
              desc="这三点决定了我们做什么，也决定了我们不做什么。"
            />
          </Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
            {positionings.map((item, index) => (
              <Reveal key={item.id} delay={index * 90}>
                <div className="h-full p-7 rounded-lg bg-background-50 border border-background-200">
                  <span className="w-12 h-12 flex items-center justify-center rounded-md bg-primary-100 text-primary-600 text-2xl">
                    <i className={item.icon}></i>
                  </span>
                  <h3 className="mt-5 font-heading text-lg font-bold text-foreground-950">
                    {item.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-16 md:py-24 bg-primary-900">
        <div className="max-w-content mx-auto">
          <Reveal>
            <SectionHeading
              eyebrow="发展理念"
              title="四条不打折的底线"
              tone="dark"
              align="center"
              desc="无论平台规模如何变化，这四条都不会因为流量或时效压力而调整。"
            />
          </Reveal>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {aboutValues.map((value, index) => (
              <Reveal key={value.id} delay={index * 80}>
                <div className="h-full p-6 rounded-lg bg-background-50/5 border border-background-50/12">
                  <span className="w-11 h-11 flex items-center justify-center rounded-md bg-accent-500/15 text-accent-300 text-xl">
                    <i className={value.icon}></i>
                  </span>
                  <h3 className="mt-5 font-heading text-lg font-bold text-background-50">
                    {value.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-background-300/90">{value.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-16 md:py-24">
        <div className="max-w-content mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <div className="lg:col-span-5">
            <Reveal>
              <SectionHeading
                eyebrow="内容原则"
                title="哪些内容会被刊发，哪些不会"
                desc="以下五条是我们处理每一条线索时的实际判断标准，也是编辑内部反复强调的共识。"
              />
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <ol className="space-y-4">
              {principles.map((principle, index) => (
                <Reveal key={principle} delay={index * 60}>
                  <li className="flex items-start gap-5 p-5 rounded-lg bg-background-100 border border-background-200">
                    <span className="font-heading text-2xl font-bold text-primary-400 shrink-0">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <p className="text-[15px] leading-relaxed text-foreground-700 pt-1">
                      {principle}
                    </p>
                  </li>
                </Reveal>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-16 md:py-20 bg-background-100">
        <div className="max-w-content mx-auto">
          <Reveal>
            <SectionHeading
              eyebrow="用户口碑"
              title="投稿者怎么说"
              align="center"
              desc="以下内容来自投稿者在事后沟通中的反馈，已隐去可识别身份的信息。"
            />
          </Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5">
            {testimonials.map((item, index) => (
              <Reveal key={item.id} delay={index * 80}>
                <figure className="h-full flex flex-col p-7 rounded-lg bg-background-50 border border-background-200">
                  <span className="w-9 h-9 flex items-center justify-center rounded-md bg-accent-100 text-accent-700 text-xl">
                    <i className="ri-double-quotes-l"></i>
                  </span>
                  <blockquote className="mt-5 text-[15px] leading-relaxed text-foreground-700 flex-1">
                    {item.quote}
                  </blockquote>
                  <figcaption className="mt-6 pt-5 border-t border-background-200">
                    <span className="block font-heading text-base font-semibold text-foreground-950">
                      {item.author}
                    </span>
                    <span className="block mt-1 text-xs text-foreground-500">{item.role}</span>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 pt-14 md:pt-20">
        <div className="max-w-content mx-auto">
          <Reveal>
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 p-6 md:p-8 rounded-lg border border-background-200 bg-background-50">
              <div className="flex flex-wrap items-center gap-x-10 gap-y-4">
                <div className="flex items-center gap-3.5">
                  <span className="w-11 h-11 flex items-center justify-center rounded-md bg-primary-100 text-primary-600 text-xl">
                    <i className="ri-mail-line"></i>
                  </span>
                  <div>
                    <span className="block text-xs text-foreground-500">投稿与咨询邮箱</span>
                    <span className="block text-sm font-medium text-foreground-950">
                      {contactEmail}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-3.5">
                  <span className="w-11 h-11 flex items-center justify-center rounded-md bg-primary-100 text-primary-600 text-xl">
                    <i className="ri-time-line"></i>
                  </span>
                  <div>
                    <span className="block text-xs text-foreground-500">响应时间</span>
                    <span className="block text-sm font-medium text-foreground-950">
                      工作日内平均 6 小时
                    </span>
                  </div>
                </div>
              </div>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md border border-primary-300 text-primary-700 font-medium hover:bg-primary-100 transition-colors whitespace-nowrap cursor-pointer shrink-0"
              >
                前往联系我们
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <CtaBand
        title="认识我们之后，欢迎把线索交给这里"
        desc="我们不会因为你说得不完整而拒绝你，也不会因为线索不够轰动而敷衍你。每一条都按同一套流程处理。"
        primaryLabel="前往投稿通道"
        primaryTo="/channels"
        secondaryLabel="查看常见问题"
        secondaryTo="/faq"
      />
    </Layout>
  );
}